import type { Media } from "@mw/shared/payload-types";
import { Orb, type OrbTextureName } from "@ui/components/core/Orb/Orb";
import { cn } from "@ui/lib/utils";
import type { ReactNode } from "react";
import Image from "next/image";

interface MobileChatFrameProps {
  children: ReactNode;
  className?: string;
  footer?: ReactNode;
  header?: ReactNode;
  phoneClassName?: string;
}

interface MobileChatAgentHeaderProps {
  avatar?: Media;
  className?: string;
  orbTexture?: OrbTextureName;
  title: string;
}

function MobileChatAgentAvatar({
  avatar,
  orbTexture = "agents-1",
  title,
}: Pick<MobileChatAgentHeaderProps, "avatar" | "orbTexture" | "title">) {
  if (avatar?.url) {
    return (
      <Image
        alt={title}
        className='tw-size-8 tw-shrink-0 tw-rounded-full tw-object-cover md:tw-size-[2.625rem]'
        height={42}
        src={avatar.url}
        width={42}
      />
    );
  }

  return (
    <div
      aria-hidden='true'
      className='tw-relative tw-size-8 tw-shrink-0 tw-overflow-hidden tw-rounded-full md:tw-size-[2.625rem]'
    >
      <Orb animated={false} image={{ sizes: "42px" }} texture={orbTexture} />
    </div>
  );
}

export function MobileChatAgentHeader({ avatar, className, orbTexture, title }: MobileChatAgentHeaderProps) {
  return (
    <div className={cn("tw-flex tw-items-center tw-gap-2 md:tw-gap-3", className)}>
      <MobileChatAgentAvatar avatar={avatar} orbTexture={orbTexture} title={title} />
      <span className='tw-type-xs tw-text-black md:tw-type-base'>{title}</span>
    </div>
  );
}

export function MobileChatFrame({ children, className, footer, header, phoneClassName }: MobileChatFrameProps) {
  return (
    <div
      className={cn(
        "tw-flex tw-size-full tw-items-center tw-justify-center tw-overflow-clip tw-rounded-2xl tw-border-0.5 tw-border-black/10 tw-bg-cream tw-p-4 md:tw-rounded-3xl md:tw-p-8",
        className,
      )}
    >
      <div
        className={cn(
          "tw-relative tw-flex tw-aspect-[2/3] tw-h-full tw-max-h-[28rem] tw-flex-col tw-justify-between tw-overflow-hidden tw-rounded-xl tw-bg-white tw-p-2.5 tw-shadow-[0_0_0.9px_0_rgba(0,0,0,0.4),0_1.8px_1.8px_0_rgba(0,0,0,0.04)] md:tw-rounded-[1.8rem] md:tw-p-3.5",
          phoneClassName,
        )}
      >
        {/* Content area */}
        <div className='tw-relative tw-min-h-0 tw-flex-1 tw-overflow-hidden'>
          {/* Gradient header overlay */}
          <div className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-10 tw-pb-6 tw-bg-[linear-gradient(180deg,#fff_35.78%,rgba(255,255,255,0)_100%)] md:tw-pb-10'>
            <div className='tw-pointer-events-auto'>{header}</div>
          </div>

          <div className='tw-h-full tw-pt-10 md:tw-pt-[3.75rem]'>{children}</div>
        </div>

        {/* Composer / footer */}
        {footer ? <div className='tw-pt-2'>{footer}</div> : null}
      </div>
    </div>
  );
}
